import { useFrame, useThree } from '@react-three/fiber';
import { PointerLockControls } from '@react-three/drei';
import { useRef, useEffect } from 'react';

import { useControls } from 'leva';

import { registerPointerLockFn, isUiOpen, hoveredProductStore, selectedProductStore, cartOpenStore, lightboxStore, clickGuard } from '../../store/uiStore';
import { IS_TOUCH } from '../../config/touch';
import { DEBUG_MODE } from '../../config/debug';
import TouchLookControls from './TouchLookControls';
import { registraRig, cfgCamera } from './cameraShake';

// Sensibilità del mouse (ripristinata dopo la finestra anti-scatto)
const POINTER_SPEED = 0.5;

// SOGLIA DI PLAUSIBILITÀ FISICA. Il browser accorpa i mousemove al ritmo dei
// fotogrammi, quindi anche una sventagliata rabbiosa fa qualche centinaio di
// px per evento; il salto del riaggancio ne porta MIGLIAIA (con 3140 si fa
// mezzo giro esatto). Un evento oltre questa soglia non l'ha prodotto una
// mano: si butta via SEMPRE, senza guardare finestre o stati. È la rete che
// regge anche se il salto arriva spezzato in piu' eventi, o se l'evento
// 'lock' non e' ancora arrivato.
const SALTO_IMPOSSIBILE = 800;

// ...ma 800 px sono comunque 46 gradi in un fotogramma (drei fa
// `_euler.y -= movementX * 0.002 * pointerSpeed`, cioe' 0.001 rad per px con
// pointerSpeed 0.5): una rete cosi' larga lascia passare mezza svolta.
// MISURATO in pagina: un solo evento da 500 px gira la visuale di 28.65 gradi
// e non lo ferma nessuno. Larga va bene mentre si gioca, dove una sventagliata
// vera puo' davvero fare qualche centinaio di px; NON va bene nei dintorni di
// un cambio di stato del pointer lock, che e' l'unico momento in cui il salto
// spurio arriva. Li' si stringe a 150 px, cioe' 8.6 gradi.
const SALTO_AL_CAMBIO = 150;
const FINESTRA_CAMBIO = 300;

export default function CameraController() {
  const { camera, scene } = useThree();
  const setEvents = useThree((s) => s.setEvents);
  const shakeGroup = useRef();
  const controlsRef = useRef();

  // FIX CRITICO: drei, quando i PointerLockControls sono connessi,
  // forza il raycast di TUTTI gli eventi R3F al CENTRO dello schermo —
  // anche a pointer libero ("ovunque clicco, clicca al centro").
  // Questo compute lo sostituisce: centro SOLO se il lock è attivo,
  // altrimenti coordinate vere del cursore. (Effect del padre = gira
  // DOPO quello di drei nel figlio, quindi vince.)
  useEffect(() => {
    setEvents({
      compute(event, state) {
        if (document.pointerLockElement) {
          state.pointer.set(0, 0);
        } else {
          state.pointer.set(
            (event.offsetX / state.size.width) * 2 - 1,
            -(event.offsetY / state.size.height) * 2 + 1
          );
        }
        state.raycaster.setFromCamera(state.pointer, state.camera);
      },
    });
  }, [setEvents]);

  // Click-per-agganciare la visuale, MA solo senza UI aperte.
  // Il handler nativo di drei (click su document → lock) IGNORA la
  // prop enabled: col prodotto in mano il click d'inizio drag
  // bloccava il pointer a listener staccati = visuale congelata.
  // Lo disattiviamo (selector che non matcha nulla) e lo
  // sostituiamo con questo, condizionato a isUiOpen().
  useEffect(() => {
    const onClick = (e) => {
      // click già consumato da una card del carosello → non è "rientra"
      if (clickGuard.consumed) {
        clickGuard.consumed = false;
        return;
      }
      if (!isUiOpen()) {
        // In FPS, click con un prodotto nel mirino = lo prendi e il
        // mouse si libera (equivalente della E)
        const hovered = hoveredProductStore.get();
        if (hovered && document.pointerLockElement) {
          selectedProductStore.set(hovered);
          document.exitPointerLock?.();
          return;
        }
        controlsRef.current?.lock();
        return;
      }
      // PRODOTTO IN MANO a cursore libero: un click sul MONDO (canvas)
      // riaggancia il pointer lock SENZA posare il prodotto — si torna
      // in FPS col prodotto in mano. Nuvoletta/lightbox/carrello esclusi.
      if (
        selectedProductStore.get() &&
        !cartOpenStore.get() &&
        lightboxStore.get() === null &&
        !document.pointerLockElement &&
        e.target instanceof HTMLCanvasElement
      ) {
        controlsRef.current?.lock();
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // Espone la lock() dei controls alla UI 2D (relockPointer in uiStore):
  // alla chiusura di pagina prodotto/carrello il mouse si riaggancia da solo
  useEffect(() => {
    registerPointerLockFn(() => controlsRef.current?.lock());
    // In ?debug i controls sono ispezionabili da console: senza, il difetto
    // del giro di 180 gradi non e' PROVABILE, perche' nel browser di prova
    // il pointer lock vero non si puo' agganciare. Con questo si puo'
    // fingere (isLocked = true) e sparare un mousemove col delta enorme.
    if (DEBUG_MODE) window.__controls = () => controlsRef.current;
    return () => registerPointerLockFn(null);
  }, []);

  // ============================================================
  // IL GIRO DI 180 GRADI QUANDO SI POSA L'OROLOGIO.
  //
  // Col polso alzato il pointer lock e' sganciato, quindi il cursore gira
  // libero per lo schermo. Chiudendo, `relockPointer()` riaggancia e Chrome
  // consegna UN mousemove che porta dentro tutto lo spostamento accumulato.
  // I PointerLockControls fanno `_euler.y -= movementX * 0.002 * pointerSpeed`:
  // con pointerSpeed 0.5 bastano ~3140 px di movementX per mezzo giro esatto.
  // E' il "giro istantaneo di 180 gradi" segnalato.
  //
  // IL RIMEDIO C'ERA MA NON SCATTAVA IN TEMPO: la sensibilita' veniva
  // azzerata NEL useFrame, cioe' al fotogramma successivo, mentre l'evento
  // spurio arriva subito dopo il `lock` — molto prima che giri un frame.
  // Quando per caso un fotogramma ci si infilava in mezzo funzionava, ed e'
  // per questo che il difetto sembrava capitare "a volte".
  //
  // Ora l'evento si butta via in DUE modi indipendenti:
  //  1. `pointerSpeed = 0` SUBITO dentro onLock (il `pointerlockchange` e'
  //     consegnato prima del primo mousemove, quindi si fa in tempo);
  //  2. il primo mousemove dopo l'aggancio viene fermato in fase di CATTURA
  //     su document. La cattura precede il bersaglio e la risalita, quindi
  //     gira PRIMA del gestore di drei (che sta su document in risalita)
  //     qualunque sia l'ordine di registrazione — e cosi' quell'evento non
  //     lo vede nessuno, nemmeno il `segna` del Player, che altrimenti lo
  //     scambierebbe per un movimento vero e rinuncerebbe al ritorno della
  //     visuale.
  // I 150 ms restano solo da tetto di sicurezza, se quel mousemove non
  // arrivasse mai.
  // ============================================================
  // ------------------------------------------------------------
  // ...E LO STESSO GIRO QUANDO SI PRENDE IL PRODOTTO.
  // Segnalato: «appena prendo il prodotto si bugga la visuale, credo faccia
  // una specie di giro particolare». E' lo stesso difetto visto dall'altra
  // parte: prendere un prodotto chiama `document.exitPointerLock()` (Hud.jsx
  // con la E, CameraController col click), e SULLO SGANCIO la protezione non
  // c'era — era armata solo dall'evento `lock`. Il salto arriva uguale, perche'
  // uguale e' la causa: Chrome rimette il cursore dov'era prima dell'aggancio e
  // quello spostamento finisce dentro un mousemove.
  // Quindi ora la guardia si arma a OGNI cambio di stato, aggancio e sgancio,
  // e nella finestra intorno al cambio la soglia di plausibilita' si stringe.
  // ------------------------------------------------------------
  const suppressUntil = useRef(0);
  const primoDopoLock = useRef(false);
  const cambioA = useRef(-Infinity);
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    const onCambio = () => {
      const ora = performance.now();
      cambioA.current = ora;
      suppressUntil.current = ora + 150;
      cfgCamera.sordinaFino = suppressUntil.current;
      primoDopoLock.current = true;
      controls.pointerSpeed = 0;
    };
    const ingoia = (e) => {
      const dx = Math.abs(e.movementX || 0);
      const dy = Math.abs(e.movementY || 0);
      const limite = performance.now() - cambioA.current < FINESTRA_CAMBIO
        ? SALTO_AL_CAMBIO : SALTO_IMPOSSIBILE;
      if (dx > limite || dy > limite) {
        // sempre, anche fuori dalla finestra del lock, e quante volte serve
        e.stopPropagation();
        return;
      }
      if (!primoDopoLock.current) return;
      primoDopoLock.current = false;
      e.stopPropagation();
      suppressUntil.current = 0;
      cfgCamera.sordinaFino = 0;
      controls.pointerSpeed = POINTER_SPEED;
    };
    controls.addEventListener('lock', onCambio);
    controls.addEventListener('unlock', onCambio);
    document.addEventListener('mousemove', ingoia, true);
    return () => {
      controls.removeEventListener('lock', onCambio);
      controls.removeEventListener('unlock', onCambio);
      document.removeEventListener('mousemove', ingoia, true);
    };
  }, []);
  useFrame(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    controls.pointerSpeed =
      performance.now() < suppressUntil.current ? 0 : POINTER_SPEED;
  });

  // Movimento della camera (respiro, passo, inerzia): i parametri
  // vivono in cameraShake.js, qui solo gli slider che li pilotano.
  // Il CALCOLO avviene nel Player, che gira dopo e conosce lo stato
  // del movimento; qui ci limitiamo a registrare il rig.
  const movimento = useControls('Camera', {
    movimentoCamera: true,
    respiro: { value: 1, min: 0, max: 3, step: 0.05 },
    passo: { value: 1, min: 0, max: 3, step: 0.05 },
    inerzia: { value: 1, min: 0, max: 3, step: 0.05 },
    tremore: { value: 1, min: 0, max: 3, step: 0.05 },
  }, { collapsed: true });
  cfgCamera.attivo = movimento.movimentoCamera;
  cfgCamera.respiro = movimento.respiro;
  cfgCamera.passo = movimento.passo;
  cfgCamera.inerzia = movimento.inerzia;
  cfgCamera.tremore = movimento.tremore;

  // Rig: la camera gli sta dentro e lui si posiziona SUL corpo, così
  // le rotazioni la fanno girare su sé stessa invece di farla
  // orbitare attorno all'origine del mondo (vedi cameraShake.js)
  useEffect(() => {
    if (!shakeGroup.current) return;
    const rig = shakeGroup.current;
    rig.add(camera);
    scene.add(rig);
    registraRig(rig);

    return () => {
      registraRig(null);
      rig.remove(camera);
      scene.remove(rig);
    };
  }, [camera, scene]);

  return (
    <>
      {IS_TOUCH
        ? <TouchLookControls />
        : <PointerLockControls
            ref={controlsRef}
            selector="#__lock-manuale__"
            maxPolarAngle={ Math.PI - 0.5}
            minPolarAngle={ 1 }
            pointerSpeed={ POINTER_SPEED }
            makeDefault
          />}
      <group ref={shakeGroup} />
    </>
  );
}